// 261010 P2-1 (luồng Sky) — panel "Sơ đồ định vị" ở CẤP PLAN (không nằm trong SkyVideoPanel): danh sách setup theo bối cảnh + ma trận gán
// video → giai đoạn → setup. Chỉ mount khi plan sky (Q1: plan khác gọi route sơ đồ ⇒ 409 NOT_SKY_PLAN, nên FE không gọi).
// Việc nền (gen ảnh / đề xuất) chạy 202: dùng lại `jobs`/`failures` mà EventPlanTab đã poll (GET /plans/:id/jobs) thay vì poll lần hai.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Input, Modal, Select, Space, Spin, Tag, message } from 'antd';
import { filmApi, type FilmBgStarted } from '../../services/api/film';
import { useFilmPlanOptional } from './FilmPlanContext';
import type { FilmCharacter, FilmCharacterLook, FilmFailedJob, FilmLocation, FilmRunningJob, PositionMapsResponse, PositionMapWriteBody, PositionMapRole } from '../../types/film';
import PositionMapCard from './PositionMapCard';
import {
  buildMatrix, countPendingChanges, driftReasonText, failureForMap, filterVisibleFailures, finishedPositionJobs, groupMapsByLocation, initDraft, isLinksChanged,
  approvalErrorText, isPositionJobType, linksPayload, positionMapErrorText, POSITION_JOB, proposeImpact, runningPositionJobs, sanitizeDraft, signatureOfRows, splitDrifted, usageLabels,
  resolveStoryId, warningText, type MatrixDraft, type MatrixScene,
  visibleZones,
} from './positionMapUtils';
import { workflowActionError, type ActionError } from './workflowActionPolicy'; // 261010 P2b-1

const OPEN_KEY = (planId: string) => `film.positionMaps.open.${planId}`;
const readOpen = (planId: string): boolean => { try { return localStorage.getItem(OPEN_KEY(planId)) === '1'; } catch { return false; } };
const writeOpen = (planId: string, v: boolean) => { try { localStorage.setItem(OPEN_KEY(planId), v ? '1' : '0'); } catch { /* private mode: chấp nhận quên */ } };

export interface PositionMapPanelProps {
  planId: string;
  scenes: MatrixScene[];
  jobs: FilmRunningJob[];
  failures: FilmFailedJob[];
  /** Hỏi lại `GET /plans/:id/jobs` NGAY (sau 202) và trả kết quả; phần poll định kỳ do EventPlanTab lo. */
  onJobsRefresh: () => Promise<{ jobs: FilmRunningJob[]; failures?: FilmFailedJob[] } | undefined>;
  /** 261010 P2b-1 (khung mới) — panel nằm NGOÀI `FilmPlanProvider` ⇒ truyền `storyId`; có prop thì thắng context. UI cũ không truyền và vẫn trong provider. */
  storyId?: string;
  /** Ẩn giá ở mọi thẻ + dòng "vài cent" của hộp đề xuất. Không truyền ⇒ UI cũ y nguyên. */
  hideCost?: boolean;
  /** Gọi sau MỖI thao tác ghi (thành công hay lỗi) để chủ nhà tải lại phần của mình (khung mới: workflow-status + dữ liệu bước). */
  onActionDone?: () => void;
  /** Lỗi nghiệp vụ đã dịch sang câu tiếng Việt (để khung mới hiện trong khối, không chỉ toast). */
  onActionError?: (e: ActionError) => void;
  /** Luôn mở thân panel (khung mới mở panel trong ngăn kéo): không nhớ ở localStorage, không có nút Thu gọn. */
  alwaysOpen?: boolean;
}

const cellBg = (drift: boolean, empty: boolean) => (drift ? '#fff1f0' : empty ? '#fffbe6' : undefined);

const PositionMapPanel: React.FC<PositionMapPanelProps> = ({ planId, scenes, jobs, failures, onJobsRefresh, storyId: storyIdProp, hideCost, onActionDone, onActionError, alwaysOpen }) => {
  const planCtx = useFilmPlanOptional();
  const storyId = resolveStoryId(storyIdProp, planCtx?.storyId);
  const done = () => { onActionDone?.(); };
  const fail = (e: any, what: string, text: string) => { message.error(text); onActionError?.(workflowActionError(e, what)); done(); };
  const [open, setOpen] = useState(() => !!alwaysOpen || readOpen(planId));
  const [resp, setResp] = useState<PositionMapsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [characters, setCharacters] = useState<FilmCharacter[]>([]);
  const [looks, setLooks] = useState<FilmCharacterLook[]>([]);
  const [locations, setLocations] = useState<FilmLocation[]>([]);
  const [draft, setDraft] = useState<MatrixDraft>({});
  const [savingLinks, setSavingLinks] = useState(false);
  const [proposing, setProposing] = useState(false);
  /** jobId (do 202 trả về = `failures[].id`) → mapId, chỉ trong phiên — `failures[]` không mang key của việc. */
  const [sessionJobs, setSessionJobs] = useState<Record<string, string>>({});
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [adding, setAdding] = useState(false);

  useEffect(() => { setOpen(!!alwaysOpen || readOpen(planId)); }, [planId, alwaysOpen]);

  const reload = useCallback(async () => {
    try {
      const [r, locs] = await Promise.all([filmApi.getPositionMaps(planId), filmApi.listLocations(storyId)]);
      setResp(r); setLocations(locs); setLoadError(null);
    } catch (e: any) {
      setLoadError(positionMapErrorText(e, 'Tải sơ đồ định vị'));
    } finally { setLoading(false); }
  }, [planId, storyId]);

  // Nhân vật + look đổi hiếm ⇒ nạp lúc mở / bấm ⟳, không nạp lại sau mỗi lần ghi setup.
  const loadCast = useCallback(async () => {
    try {
      const [c, l] = await Promise.all([filmApi.listCharacters(storyId), filmApi.listLooks(storyId)]);
      setCharacters(c); setLooks(l);
    } catch { /* thiếu danh sách thì chỉ mất ô chọn look; thẻ vẫn hiện tên rút gọn */ }
  }, [storyId]);
  useEffect(() => { loadCast(); }, [loadCast]);

  // Cấu trúc plan đổi (P1 tách/gộp/hạ event, thêm giai đoạn) ⇒ liên kết có thể lệch ⇒ nạp lại. Chạy cả lần mount.
  const scenesSig = useMemo(() => scenes.map((s) => `${s.id}:${s.events.map((e) => `${e.id}/${e.eventOrder}/${(e.videoMeta?.phases || []).map((p) => p.order).join('.')}`).join(',')}`).join('|'), [scenes]);
  useEffect(() => { reload(); }, [reload, scenesSig]);

  // Job sơ đồ vừa XONG (có ở lượt poll trước, mất ở lượt này) ⇒ nạp lại. KHÔNG phụ thuộc isDirty của editor plan: panel này không phải nháp.
  const prevPos = useRef<FilmRunningJob[] | null>(null);
  useEffect(() => {
    const now = runningPositionJobs(jobs);
    if (finishedPositionJobs(prevPos.current, now).length) reload();
    prevPos.current = now;
  }, [jobs, reload]);

  const maps = useMemo(() => resp?.maps || [], [resp]);
  const rows = useMemo(() => (resp ? buildMatrix(scenes, resp.links.valid, resp.links.drifted, resp.uncoveredStages) : []), [resp, scenes]);
  const { orphans } = useMemo(() => splitDrifted(rows, resp?.links.drifted || []), [rows, resp]);
  const baseSig = resp ? `${resp.linksVersion}#${signatureOfRows(rows)}` : '';
  // Bản nháp chỉ reset khi liên kết đã lưu / tập hàng đổi (baseSig), KHÔNG reset mỗi lần nạp lại setup (sẽ mất ô người đang sửa dở).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  useEffect(() => { setDraft(initDraft(rowsRef.current)); }, [baseSig]);
  const mapIdsSig = maps.map((m) => m.id).join(',');
  useEffect(() => { setDraft((d) => sanitizeDraft(d, new Set(mapIdsSig ? mapIdsSig.split(',') : []))); }, [mapIdsSig]);

  const runningMapIds = useMemo(() => new Set(jobs.filter((j) => j.type === POSITION_JOB.MAP).map((j) => j.key)), [jobs]);
  const setupsRunning = jobs.some((j) => j.type === POSITION_JOB.SETUPS);
  const visibleFailures = useMemo(() => filterVisibleFailures(failures).filter((f) => isPositionJobType(f.type)), [failures]);
  const groups = useMemo(() => groupMapsByLocation(maps, locations), [maps, locations]);
  const pending = countPendingChanges(rows, draft, orphans);
  const withImage = maps.filter((m) => m.imageUrl).length;
  const coveredN = rows.filter((r) => r.mapId).length;
  const driftedN = (resp?.links.drifted.length || 0);

  /** Sau 202: ghi nhớ jobId→map, hỏi jobs NGAY; nếu việc đã xong/chết trước khi thấy nó chạy thì nạp lại luôn. */
  const afterStarted = async (started: FilmBgStarted, mapId?: string) => {
    const jobId = started.jobId;
    if (jobId && mapId) setSessionJobs((s) => ({ ...s, [jobId]: mapId }));
    const r = await onJobsRefresh();
    if (jobId && r && !r.jobs.some((j) => j.id === jobId)) await reload();
  };

  const errorOf = (mapId: string): string | null => {
    const f = failureForMap(mapId, sessionJobs, visibleFailures);
    if (!f) return null;
    const map = maps.find((m) => m.id === mapId);
    // Đã có ảnh MỚI hơn lỗi này ⇒ lỗi không còn nói lên hiện trạng.
    if (map?.imageAt && f.endedAt && new Date(map.imageAt).getTime() > new Date(f.endedAt).getTime()) return null;
    return f.note;
  };

  const patchMap = async (id: string, body: PositionMapWriteBody): Promise<boolean> => {
    try { await filmApi.patchPositionMap(id, body); await reload(); done(); return true; }
    catch (e: any) { fail(e, 'Lưu setup', positionMapErrorText(e, 'Lưu setup')); if (e?.response?.data?.code === 'NOT_FOUND') await reload(); return false; }
  };

  const generateMap = async (id: string, name: string) => {
    try {
      const started = await filmApi.generatePositionMap(id);
      message.info(`Đang gen ảnh "${name}" ở nền (~40–110 giây).`);
      await afterStarted(started, id);
      done();
    } catch (e: any) { fail(e, hideCost ? `Tạo ảnh "${name}"` : `Gen ảnh "${name}"`, positionMapErrorText(e, `Gen ảnh "${name}"`)); }
  };

  // 261010 P4-1 — cổng duyệt. 409 STALE/NO_IMAGE nghĩa là hiển thị đã lỗi thời (ai đó vừa sửa/gen) ⇒ nạp lại để nút khoá đúng.
  const approveMap = async (id: string, name: string) => {
    try { await filmApi.approvePositionMap(id); message.success(`Đã duyệt "${name}" — từ nay được đính vào video.`); await reload(); done(); }
    catch (e: any) {
      fail(e, `Duyệt "${name}"`, approvalErrorText(e, `Duyệt "${name}"`, positionMapErrorText));
      const code = e?.response?.data?.code;
      if (code === 'STALE' || code === 'NO_IMAGE' || code === 'NOT_FOUND') await reload();
    }
  };

  const unapproveMap = async (id: string, name: string) => {
    try { await filmApi.unapprovePositionMap(id); message.success(`Đã bỏ duyệt "${name}"`); await reload(); done(); }
    catch (e: any) { fail(e, `Bỏ duyệt "${name}"`, approvalErrorText(e, `Bỏ duyệt "${name}"`, positionMapErrorText)); if (e?.response?.data?.code === 'NOT_FOUND') await reload(); }
  };

  const deleteMap = async (id: string, name: string) => {
    try { await filmApi.deletePositionMap(id); message.success(`Đã xoá "${name}"`); await reload(); done(); }
    catch (e: any) { fail(e, `Xoá "${name}"`, positionMapErrorText(e, `Xoá "${name}"`)); }
  };

  const doPropose = async () => {
    setProposing(true);
    try {
      const started = await filmApi.proposePositionMaps(planId);
      message.info('AI đang đề xuất setup ở nền (~1–2 phút).');
      await afterStarted(started);
      done();
    } catch (e: any) { fail(e, 'Đề xuất setup', positionMapErrorText(e, 'Đề xuất setup')); }
    finally { setProposing(false); }
  };

  const askPropose = () => {
    if (!maps.length) { doPropose(); return; }
    const { replaced, kept } = proposeImpact(maps);
    Modal.confirm({
      title: 'AI đề xuất lại setup?',
      width: 520,
      okText: 'Đề xuất lại', cancelText: 'Huỷ',
      content: (
        <div style={{ fontSize: 13 }}>
          <div style={{ marginBottom: 6 }}><b>Sẽ bị thay ({replaced.length})</b> — do AI viết và chưa có ảnh:</div>
          <div style={{ marginBottom: 8 }}>{replaced.length ? replaced.map((n) => <Tag key={n} color="orange">{n}</Tag>) : <i>không có</i>}</div>
          <div style={{ marginBottom: 6 }}><b>Được giữ ({kept.length})</b> — đã sửa (tay hoặc agent) hoặc đã có ảnh, cùng liên kết của chúng:</div>
          <div style={{ marginBottom: 8 }}>{kept.length ? kept.map((n) => <Tag key={n} color="green">{n}</Tag>) : <i>không có</i>}</div>
          {!hideCost && <div style={{ color: '#888', fontSize: 12 }}>Chỉ tốn một lượt AI chữ (vài cent); không gen ảnh.</div>}
        </div>
      ),
      onOk: doPropose,
    });
  };

  const saveLinks = async () => {
    if (!resp) return;
    setSavingLinks(true);
    try {
      await filmApi.putPositionMapLinks(planId, resp.linksVersion, linksPayload(rows, draft));
      message.success('Đã lưu liên kết');
      await reload();
      done();
    } catch (e: any) {
      if (isLinksChanged(e)) { await reload(); message.warning({ content: positionMapErrorText(e, 'Lưu liên kết'), duration: 10 }); onActionError?.(workflowActionError(e, 'Lưu liên kết')); done(); }
      else fail(e, 'Lưu liên kết', positionMapErrorText(e, 'Lưu liên kết'));
    } finally { setSavingLinks(false); }
  };

  const toggle = () => { const v = !open; setOpen(v); if (!alwaysOpen) writeOpen(planId, v); };

  const unattributed = visibleFailures
    .filter((f) => !dismissed.includes(f.id) && (f.type === POSITION_JOB.SETUPS || !sessionJobs[f.id]))
    .slice(0, 3);

  const mapOptions = groups.map((g) => ({ label: g.name, options: g.maps.map((m) => ({ value: m.id, label: `${m.name} (${m.role === 'ANCHOR' ? 'chính' : 'phụ'})` })) }));
  const mapName = (id: string | null | undefined) => maps.find((m) => m.id === id)?.name || '(setup đã xoá)';

  const header = (
    <Space wrap size={8}>
      <b>🗺 Sơ đồ định vị</b>
      {resp && (
        <span style={{ fontSize: 12, color: '#666' }}>
          {maps.length} setup · {withImage} có ảnh · {coveredN}/{rows.length} giai đoạn đã gán
        </span>
      )}
      {driftedN > 0 && <Tag color="error" style={{ margin: 0 }}>{driftedN} liên kết lệch</Tag>}
      {resp && rows.length - coveredN > 0 && <Tag color="warning" style={{ margin: 0 }}>{rows.length - coveredN} giai đoạn chưa có setup</Tag>}
      {(setupsRunning || runningMapIds.size > 0) && <Tag color="processing" style={{ margin: 0 }}><Spin size="small" style={{ marginRight: 4 }} />{setupsRunning ? 'AI đang đề xuất' : `đang gen ${runningMapIds.size} ảnh`}</Tag>}
    </Space>
  );

  return (
    <Card size="small" style={{ marginBottom: 12 }} data-testid="position-map-panel" title={header}
      extra={(
        <Space size={6} wrap>
          <Button size="small" onClick={askPropose} loading={proposing} disabled={!resp || setupsRunning}>AI đề xuất setup</Button>
          <Button size="small" onClick={() => setAdding(true)} disabled={!resp}>+ Thêm setup</Button>
          <Button size="small" onClick={() => { reload(); loadCast(); }} loading={loading}>⟳</Button>
          {!alwaysOpen && <Button size="small" type="link" onClick={toggle}>{open ? 'Thu gọn' : 'Mở'}</Button>}
        </Space>
      )}
      styles={open ? undefined : { body: { display: 'none' } }}>
      {loadError && <Alert type="error" showIcon message={loadError} style={{ marginBottom: 8 }} />}
      {unattributed.map((f) => (
        <Alert key={f.id} type="error" showIcon closable style={{ marginBottom: 8 }} onClose={() => setDismissed((d) => [...d, f.id])}
          message={`${f.type === POSITION_JOB.SETUPS ? 'Đề xuất setup' : 'Gen ảnh sơ đồ'} thất bại — ${f.minutesAgo}′ trước`}
          description={<span style={{ fontSize: 12 }}>{f.note}{f.type === POSITION_JOB.MAP ? ' (không rõ setup nào — gen lại từ thẻ để thấy lỗi ngay trên thẻ)' : ''}</span>} />
      ))}
      {resp && resp.warnings.length > 0 && (
        <Alert type="warning" showIcon style={{ marginBottom: 8 }} message="Cảnh báo" description={<ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>{resp.warnings.map((w) => <li key={w}>{warningText(w)}</li>)}</ul>} />
      )}
      {loading && !resp && <div style={{ textAlign: 'center', padding: 16 }}><Spin /></div>}

      {resp && maps.length === 0 && (
        <Alert type="info" showIcon style={{ marginBottom: 8 }} message="Plan chưa có setup nào" description='Bấm "AI đề xuất setup" để AI gom giai đoạn thành các thế đứng, hoặc "+ Thêm setup" để tự tạo. Gen ảnh là bước riêng (tốn tiền) trên từng thẻ.' />
      )}

      {groups.map((g) => (
        <div key={g.key} style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, margin: '4px 0 6px' }}>📍 {g.name} <span style={{ fontWeight: 400, color: '#888', fontSize: 12 }}>· {g.maps.length} setup</span></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', gap: 12 }}>
            {g.maps.map((m) => (
              <PositionMapCard key={m.id} map={m} allMaps={maps} characters={characters} looks={looks} locations={locations}
                usage={usageLabels(m.id, rows)} running={runningMapIds.has(m.id)} error={errorOf(m.id)}
                onPatch={(body) => patchMap(m.id, body)} onGenerate={() => generateMap(m.id, m.name)} onDelete={() => deleteMap(m.id, m.name)}
                onApprove={() => approveMap(m.id, m.name)} onUnapprove={() => unapproveMap(m.id, m.name)} hideCost={hideCost} />
            ))}
          </div>
        </div>
      ))}

      {resp && rows.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            Gán setup cho video / giai đoạn
            <span style={{ fontWeight: 400, fontSize: 12, color: '#888' }}>— đỏ: liên kết lệch · vàng: chưa có setup</span>
            <span style={{ flex: 1 }} />
            {(pending.changed > 0 || pending.dropped > 0) && (
              <span style={{ fontSize: 12, color: '#d46b08' }}>
                {pending.changed > 0 ? `${pending.changed} ô đổi` : ''}{pending.changed > 0 && pending.dropped > 0 ? ' · ' : ''}{pending.dropped > 0 ? `${pending.dropped} liên kết lệch sẽ bị bỏ` : ''}
              </span>
            )}
            <Button size="small" onClick={() => setDraft(initDraft(rows))} disabled={pending.changed === 0}>Hoàn tác</Button>
            <Button size="small" type="primary" loading={savingLinks} disabled={(pending.changed === 0 && pending.dropped === 0) || setupsRunning} onClick={saveLinks}>Lưu liên kết</Button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="pm-matrix" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#fafafa', textAlign: 'left' }}>
                  <th style={{ padding: '4px 8px', width: 90 }}>Video</th>
                  <th style={{ padding: '4px 8px' }}>Giai đoạn</th>
                  <th style={{ padding: '4px 8px', width: 280 }}>Setup</th>
                  <th style={{ padding: '4px 8px', width: 280 }}>Ghi chú</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const cur = draft[r.key] ?? null;
                  const bg = cellBg(!!r.drift && !cur, !cur && !r.drift);
                  return (
                    <tr key={r.key} data-testid={`pm-row-${r.key}`} style={{ borderTop: '1px solid #f0f0f0', background: bg }}>
                      <td style={{ padding: '3px 8px', whiteSpace: 'nowrap' }}>{r.sceneLabel} · {r.eventLabel}</td>
                      <td style={{ padding: '3px 8px' }}>{r.stageLabel}</td>
                      <td style={{ padding: '3px 8px' }}>
                        <Select size="small" style={{ width: '100%' }} allowClear placeholder="— chưa gán —" value={cur || undefined} options={mapOptions}
                          status={r.drift && !cur ? 'error' : undefined}
                          onChange={(v) => setDraft((d) => ({ ...d, [r.key]: v || null }))} />
                      </td>
                      <td style={{ padding: '3px 8px' }}>
                        {r.drift && !cur ? (
                          <span style={{ color: '#cf1322' }}>
                            Lệch: {driftReasonText(r.drift.reason)} (trước: {mapName(r.drift.mapId)}){' '}
                            {maps.some((m) => m.id === r.drift!.mapId) && <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setDraft((d) => ({ ...d, [r.key]: r.drift!.mapId }))}>Giữ setup này</Button>}
                          </span>
                        ) : !cur ? <span style={{ color: '#d48806' }}>chưa có setup</span> : (r.mapId !== cur ? <span style={{ color: '#d46b08' }}>chưa lưu</span> : null)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {orphans.length > 0 && (
            <Alert type="error" showIcon style={{ marginTop: 8 }} message={`${orphans.length} liên kết lệch không còn hàng tương ứng — sẽ bị bỏ khi lưu`}
              description={<ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>{orphans.map((o, i) => (
                <li key={i}>video {o.link.eventId.slice(0, 6)}… {o.link.phaseOrder === null ? '(cả video)' : `GĐ${o.link.phaseOrder}`} → {mapName(o.link.positionMapId)}: {driftReasonText(o.reason)}</li>
              ))}</ul>} />
          )}
        </div>
      )}

      <AddMapModal open={adding} onClose={() => setAdding(false)} locations={locations} characters={characters} maps={maps}
        onCreate={async (body) => {
          try { await filmApi.createPositionMap(planId, body); message.success('Đã thêm setup'); await reload(); done(); return true; }
          catch (e: any) { fail(e, 'Thêm setup', positionMapErrorText(e, 'Thêm setup')); return false; }
        }} />
    </Card>
  );
};

// ───────────────────────── Modal thêm setup tay ─────────────────────────

const AddMapModal: React.FC<{
  open: boolean; onClose: () => void; locations: FilmLocation[]; characters: FilmCharacter[]; maps: PositionMapsResponse['maps'];
  onCreate: (body: PositionMapWriteBody) => Promise<boolean>;
}> = ({ open, onClose, locations, characters, maps, onCreate }) => {
  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState<string | undefined>();
  const [role, setRole] = useState<PositionMapRole>('ANCHOR');
  const [anchorMapId, setAnchorMapId] = useState<string | undefined>();
  const [zoneId, setZoneId] = useState<string | undefined>();
  const [text, setText] = useState('');
  const [castIds, setCastIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const loc = locations.find((l) => l.id === locationId);
  const anchors = maps.filter((m) => m.role === 'ANCHOR' && m.locationId === locationId);
  const reset = () => { setName(''); setLocationId(undefined); setRole('ANCHOR'); setAnchorMapId(undefined); setZoneId(undefined); setText(''); setCastIds([]); };
  const valid = !!name.trim() && !!text.trim() && !!locationId && (role === 'ANCHOR' || !!anchorMapId);
  return (
    <Modal open={open} title="Thêm setup" okText="Thêm" cancelText="Huỷ" confirmLoading={busy} okButtonProps={{ disabled: !valid }} onCancel={() => { onClose(); }} destroyOnHidden
      onOk={async () => {
        setBusy(true);
        const ok = await onCreate({
          name: name.trim(), role, locationId: locationId!, zoneId: zoneId || null, anchorMapId: role === 'DERIVATIVE' ? anchorMapId! : null,
          blockingText: text.trim(), castJson: castIds.map((characterId) => ({ characterId, lookId: null, lookSource: 'MAIN' as const })),
        });
        setBusy(false);
        if (ok) { reset(); onClose(); }
      }}>
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <Input placeholder="Tên setup, vd 01_gate_approach" value={name} onChange={(e) => setName(e.target.value)} />
        <Select style={{ width: '100%' }} placeholder="Bối cảnh" value={locationId} options={locations.map((l) => ({ value: l.id, label: l.name }))}
          onChange={(v) => { setLocationId(v); setAnchorMapId(undefined); setZoneId(undefined); }} />
        <Select style={{ width: '100%' }} value={role} onChange={setRole} options={[{ value: 'ANCHOR', label: 'Ảnh chính (mỗi bối cảnh một cái)' }, { value: 'DERIVATIVE', label: 'Ảnh phụ (neo vào ảnh chính)' }]} />
        {role === 'DERIVATIVE' && (
          <Select style={{ width: '100%' }} placeholder="Neo vào ảnh chính" value={anchorMapId} onChange={setAnchorMapId} options={anchors.map((m) => ({ value: m.id, label: m.name }))}
            notFoundContent="Bối cảnh này chưa có setup chính" />
        )}
        {visibleZones(loc?.zones).length > 0 && (
          <Select style={{ width: '100%' }} allowClear placeholder="Zone (tuỳ chọn)" value={zoneId} onChange={setZoneId} options={visibleZones(loc?.zones).map((z) => ({ value: z.id, label: z.name }))} />
        )}
        <Select mode="multiple" style={{ width: '100%' }} placeholder="Nhân vật có mặt" value={castIds} onChange={setCastIds} optionFilterProp="label" options={characters.map((c) => ({ value: c.id, label: c.name }))} />
        <Input.TextArea autoSize={{ minRows: 4, maxRows: 10 }} placeholder="Chữ dàn cảnh: ai đứng đâu (mét, trái/phải), hướng nhìn, đang làm gì. Đừng tả trang phục." value={text} onChange={(e) => setText(e.target.value)} />
      </Space>
    </Modal>
  );
};

export default PositionMapPanel;
